// Minimal Telegram WebApp wrapper. The @telegram-apps/sdk-react package is
// available, but for the API surface we need (initData, MainButton, BackButton,
// haptics, cloudStorage, openInvoice) the global `window.Telegram.WebApp`
// object from the official telegram-web-app.js script is simpler and avoids
// version churn. We can drop in the SDK later if we need its richer hooks.

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { user?: { id: number; first_name: string; username?: string } };
  ready(): void;
  expand(): void;
  close(): void;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  MainButton: {
    text: string;
    setText(text: string): void;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
    isVisible: boolean;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'success' | 'warning' | 'error'): void;
    selectionChanged(): void;
  };
  CloudStorage: {
    setItem(key: string, value: string, cb?: (err: string | null, ok: boolean) => void): void;
    getItem(key: string, cb: (err: string | null, value: string) => void): void;
    removeItem(key: string, cb?: (err: string | null, ok: boolean) => void): void;
  };
  openInvoice(url: string, cb: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void): void;
  openTelegramLink(url: string): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getTelegram(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function isInTelegram(): boolean {
  return Boolean(getTelegram()?.initData);
}

export function initTelegram(): void {
  const tg = getTelegram();
  if (!tg) return;
  tg.ready();
  tg.expand();
  tg.setHeaderColor('#0A0F1F');
  tg.setBackgroundColor('#0A0F1F');
}

export const haptic = {
  tap(): void {
    getTelegram()?.HapticFeedback.impactOccurred('light');
  },
  success(): void {
    getTelegram()?.HapticFeedback.notificationOccurred('success');
  },
  error(): void {
    getTelegram()?.HapticFeedback.notificationOccurred('error');
  },
  warn(): void {
    getTelegram()?.HapticFeedback.notificationOccurred('warning');
  },
  select(): void {
    getTelegram()?.HapticFeedback.selectionChanged();
  },
};

const LS_FALLBACK_PREFIX = 'punditxi:';

export const storage = {
  async set(key: string, value: string): Promise<void> {
    const tg = getTelegram();
    if (tg?.CloudStorage) {
      await new Promise<void>((resolve, reject) =>
        tg.CloudStorage.setItem(key, value, (err) => (err ? reject(new Error(err)) : resolve())),
      );
      return;
    }
    localStorage.setItem(LS_FALLBACK_PREFIX + key, value);
  },
  async get(key: string): Promise<string | null> {
    const tg = getTelegram();
    if (tg?.CloudStorage) {
      return new Promise<string | null>((resolve) =>
        tg.CloudStorage.getItem(key, (_err, value) => resolve(value || null)),
      );
    }
    return localStorage.getItem(LS_FALLBACK_PREFIX + key);
  },
  async remove(key: string): Promise<void> {
    const tg = getTelegram();
    if (tg?.CloudStorage) {
      await new Promise<void>((resolve) =>
        tg.CloudStorage.removeItem(key, () => resolve()),
      );
      return;
    }
    localStorage.removeItem(LS_FALLBACK_PREFIX + key);
  },
};

export function useMainButton(text: string, onClick: () => void, visible = true): void {
  if (typeof window === 'undefined') return;
  const tg = getTelegram();
  if (!tg) return;
  tg.MainButton.setText(text);
  if (visible) tg.MainButton.show();
  tg.MainButton.onClick(onClick);
}
